import React, { useEffect } from 'react'
import { AnyAction } from 'redux'
import {
  useDispatch as useReduxDispatch,
  useSelector as useReduxSelector,
  type TypedUseSelectorHook,
} from 'react-redux'
import type { ThunkAction, ThunkDispatch } from 'redux-thunk'

// Host apps pass their own store selectors. `any` keeps those call sites
// valid without this package importing the app store.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RootState = any
type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>
type WebappThunkAction = ThunkAction<
  Promise<void>,
  RootState,
  unknown,
  AnyAction
>

const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector
const useDispatch: () => AppDispatch = useReduxDispatch

interface Props<
  S = RootState,
  A =
    | AnyAction
    | WebappThunkAction
    | ((dispatch: AppDispatch, getState: () => RootState) => Promise<void>),
> extends ReactChildrenProps {
  predicateSelector: (state: S) => boolean
  getDataAction?: A
  onboardingComponent: React.ReactNode
  loadingSelector?: (state: S) => boolean
  loadingComponent?: React.ReactNode
}

export const OnboardingWrapper: React.FC<Props> = ({
  predicateSelector,
  getDataAction,
  onboardingComponent: OnboardingComponent,
  loadingSelector,
  loadingComponent: LoadingComponent,
  children,
}) => {
  const onboarded = useSelector<boolean>(predicateSelector)
  const _loadingSelector = loadingSelector ? loadingSelector : () => false
  const loading = useSelector<boolean>(_loadingSelector)
  LoadingComponent = LoadingComponent ? LoadingComponent : <></>
  const dispatch = useDispatch()
  useEffect(() => {
    if (!onboarded && getDataAction) {
      dispatch(getDataAction)
    }
  }, [onboarded])
  return (
    <>
      {loading && LoadingComponent}
      {!loading && (
        <>
          <>{!onboarded && OnboardingComponent}</>
          <>{onboarded && children}</>
        </>
      )}
    </>
  )
}
